/**
 * Eixo do tempo proporcional (substitui o slider por índice): marcas de hora
 * alinhadas a Brasília, fronteiras de dia, buracos onde faltaram imagens,
 * marcador "agora", faixa dos quadros ainda sem focos do INPE e, com mais de
 * uma camada de imagem, os trechos em que cada camada ficou sem imagem.
 *
 * O eixo começa no primeiro quadro e termina no último: a imagem mais recente
 * fica no fim da barra, e nada é desenhado além dela.
 *
 * Acessível como slider: setas ±1 quadro, PageUp/PageDown ±6, Home/End.
 * O arrasto captura o ponteiro (não vaza para o pan do mapa) e é um atalho,
 * nunca o único caminho (os botões ‹ › continuam na barra).
 *
 * À direita do eixo pode haver a zona da previsão (`tracking/ZonaDaPrevisao`).
 * Este componente não sabe dela: quem o monta passa `cursorFora` (o cursor
 * está lá, e aqui fica só o quadro de origem, vazado), `aoBuscarObservado`
 * (um toque no eixo, antes de ir ao quadro) e `teclaAntes` (a zona trata a
 * tecla primeiro).
 */
import clsx from 'clsx';
import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { getProductName } from '../config/products';
import { formatDayTimeBrt, formatShortDateBrt, formatTimeBrt } from '../lib/time';
import { usePlanStore } from '../loop/plan';
import type { TrechoSemBase } from '../loop/semNoaa';
import { goToFirst, goToFrame, goToLast, stepFrame } from '../loop/playback';
import { useLoopStore } from '../loop/store';
import { useGoesStore } from '../state/goes';
import { useTimeStore } from '../state/time';
import { useNow } from './hooks';
import { axisDomain, frameAtPosition, frameGaps, hourTicks, missingSegments, positionOf, type AxisDomain } from './axisGeometry';

const pct = (p: number) => `${(p * 100).toFixed(3)}%`;

function span(a: number, b: number, d: AxisDomain): { left: string; width: string } {
  const l = positionOf(a, d);
  const r = positionOf(b, d);
  return { left: pct(l), width: pct(Math.max(0, r - l)) };
}

/**
 * Trechos SEM imagem de cada camada (só com 2+ camadas no plano), marcados
 * DENTRO do trilho, na metade de baixo. Antes cada camada ganhava uma linha
 * verde própria sob o eixo: com duas camadas pareciam duas barras de download
 * — a barra de download é uma só, a linha fina verde sob o slider.
 */
function LayerGaps(props: { d: AxisDomain; frames: readonly number[]; compact?: boolean }) {
  const layers = usePlanStore((s) => s.plan?.layers ?? null);
  const gaps = useMemo(() => {
    // Camadas ainda sem listagem (`pending`) não têm instantes: seriam lidas
    // como "sem imagem o tempo todo" justo ao adicionar uma camada.
    const active = (layers ?? []).filter((l) => l.visible && !l.pending);
    // Com uma camada só, só se não for a base (a linha do tempo é dela): com a base escondida, uma
    // camada de cima sem a mesma varredura deixa o quadro sem satélite, e o eixo mostra onde.
    if (active.length === 0 || (active.length === 1 && active[0].uid === layers?.[0]?.uid)) return [];
    return active.flatMap((l) =>
      missingSegments(l.times, props.frames).map(([a, b]) => ({
        key: `${l.uid}:${a}`,
        name: getProductName(l.productId),
        a,
        b,
      })),
    );
  }, [layers, props.frames]);
  if (gaps.length === 0) return null;
  return (
    <>
      {gaps.map((g) => {
        const text = `${g.name}: sem imagem ${g.a === g.b ? `às ${formatTimeBrt(g.a)}` : `entre ${formatTimeBrt(g.a)} e ${formatTimeBrt(g.b)}`} (Brasília)`;
        const l = positionOf(g.a, props.d);
        const w = Math.max(0, positionOf(g.b, props.d) - l);
        return (
          <span
            key={g.key}
            // Cinza claro, não amarelo: o amarelo já quer dizer "a NOAA não
            // publicou a imagem" (buraco) e "focos ainda não publicados".
            className={clsx('absolute h-0.5 bg-ink-200/90', props.compact ? 'top-[14px]' : 'top-[11px]')}
            // Falta no último quadro: a marca mínima entraria pela borda; puxa para dentro.
            style={{ left: pct(l), width: pct(w), minWidth: '2px', transform: l >= 1 ? 'translateX(-2px)' : undefined }}
            role="img"
            aria-label={text}
            title={text}
          />
        );
      })}
    </>
  );
}

const SEM_TRECHOS: readonly TrechoSemBase[] = [];

export function TimeAxis(props: {
  compact?: boolean;
  pendingFrom: number;
  footer?: ReactNode;
  /** O cursor do tempo está fora deste eixo (na zona da previsão): o quadro na tela fica marcado vazado. */
  cursorFora?: boolean;
  /** Chamado num toque ou clique no eixo, antes de ir ao quadro. */
  aoBuscarObservado?: () => void;
  /** Vê a tecla antes do eixo; verdadeiro = já tratada (o eixo não anda). */
  teclaAntes?: (tecla: string) => boolean;
}) {
  const frames = useTimeStore((s) => s.frames);
  const index = useTimeStore((s) => s.index);
  const currentTime = useTimeStore((s) => s.currentTime);
  const range = useTimeStore((s) => s.range);
  const stepMin = useLoopStore((s) => s.step);
  const semBaseDesde = usePlanStore((s) => s.plan?.semBaseDesde ?? null);
  const trechosSemBase = usePlanStore((s) => s.plan?.trechosSemBase ?? SEM_TRECHOS);
  const baseNome = useGoesStore((s) => (s.layers[0] ? getProductName(s.layers[0].productId) : null));
  const now = useNow(30_000);
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [hover, setHover] = useState<number | null>(null);

  const d = useMemo(() => axisDomain(frames, range, now), [frames, range, now]);
  const ticks = useMemo(() => (d ? hourTicks(d, props.compact ? 14 : 26) : []), [d, props.compact]);
  const gaps = useMemo(() => frameGaps(frames, stepMin * 60_000), [frames, stepMin]);
  const count = frames.length;
  const disabled = count < 2 || !d;

  const positionFromEvent = (e: PointerEvent): number => {
    const rect = ref.current!.getBoundingClientRect();
    return (e.clientX - rect.left) / Math.max(1, rect.width);
  };
  const seek = (e: PointerEvent) => {
    if (!d) return;
    goToFrame(frameAtPosition(frames, d, positionFromEvent(e)));
  };
  const onPointerDown = (e: PointerEvent) => {
    if (disabled || e.button !== 0) return;
    dragging.current = true;
    ref.current?.setPointerCapture(e.pointerId);
    ref.current?.focus({ preventScroll: true });
    props.aoBuscarObservado?.();
    seek(e);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (dragging.current) seek(e);
    else if (d && e.pointerType === 'mouse') setHover(d.start + Math.max(0, Math.min(1, positionFromEvent(e))) * (d.end - d.start));
  };
  const onPointerUp = (e: PointerEvent) => {
    if (!dragging.current) return;
    dragging.current = false;
    ref.current?.releasePointerCapture(e.pointerId);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (disabled) return;
    if (props.teclaAntes?.(e.key)) {
      e.preventDefault();
      return;
    }
    const map: Record<string, () => void> = {
      ArrowLeft: () => stepFrame(-1),
      ArrowDown: () => stepFrame(-1),
      ArrowRight: () => stepFrame(1),
      ArrowUp: () => stepFrame(1),
      PageDown: () => stepFrame(-6),
      PageUp: () => stepFrame(6),
      Home: goToFirst,
      End: goToLast,
    };
    const fn = map[e.key];
    if (!fn) return;
    e.preventDefault();
    fn();
  };

  const valueText = currentTime !== null ? `${formatShortDateBrt(currentTime)} ${formatTimeBrt(currentTime)} (Brasília)` : '';
  // Compacto (celular): traço mais alto dentro do alvo de 44 px; os rótulos (`footer`) ocupam a parte de baixo.
  const trackTop = props.compact ? 'top-[12px]' : 'top-[9px]';

  return (
    <div className="min-w-0 flex-1">
      <div
        ref={ref}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label="Quadro da animação"
        aria-valuemin={0}
        aria-valuemax={Math.max(0, count - 1)}
        aria-valuenow={index}
        aria-valuetext={valueText}
        aria-disabled={disabled || undefined}
        aria-orientation="horizontal"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => setHover(null)}
        onKeyDown={onKeyDown}
        className={clsx(
          'relative w-full touch-none select-none rounded-md outline-none focus-visible:ring-2 focus-visible:ring-mata-400',
          // Alvo de 44 px no toque; o traço continua com 4 px.
          props.compact ? 'h-11 cursor-pointer' : 'h-[22px] cursor-pointer',
          disabled && 'cursor-default opacity-50',
        )}
      >
        {/* trilho */}
        <div className={clsx('absolute inset-x-0 h-1 rounded-full bg-ink-700', trackTop)} aria-hidden />
        {d && count > 0 && (
          <>
            {/* período com quadros */}
            <div className={clsx('absolute h-1 rounded-full bg-ink-500', trackTop)} style={span(frames[0], frames[count - 1], d)} aria-hidden />
            {/* buracos: imagens que a NOAA não publicou */}
            {gaps.map(([a, b], i) => (
              <div
                key={i}
                className={clsx('absolute h-1 bg-yellow-400/70', trackTop)}
                style={span(a, b, d)}
                role="img"
                aria-label={`Sem imagens entre ${formatTimeBrt(a)} e ${formatTimeBrt(b)}`}
                title={`Sem imagens entre ${formatTimeBrt(a)} e ${formatTimeBrt(b)} (Brasília)`}
              />
            ))}
            {/* trechos em que a linha do tempo segue sem a base (loop/semNoaa.ts); mais claro que um buraco, para o "sem focos" listrado aparecer por cima */}
            {trechosSemBase.map((tr) => {
              const fim = tr.desde === semBaseDesde;
              const texto = `${baseNome ? `“${baseNome}”: ` : ''}${fim ? `parada na NOAA desde ${formatDayTimeBrt(tr.desde)}` : `sem imagem da NOAA entre ${formatDayTimeBrt(tr.desde)} e ${formatDayTimeBrt(tr.ate)}`} (Brasília). As outras camadas seguem no horário.`;
              return (
                <div
                  key={tr.desde}
                  className={clsx('absolute h-1 bg-yellow-400/40', fim && 'rounded-r-full', trackTop)}
                  style={span(tr.desde, tr.ate, d)}
                  role="img"
                  aria-label={texto}
                  title={texto}
                />
              );
            })}
            {/* quadros ainda sem focos do INPE (até o último quadro, nunca além dele) */}
            {props.pendingFrom >= 0 && props.pendingFrom < count && (
              <div
                className={clsx('pending-fires absolute h-1 rounded-r-full', trackTop)}
                style={span(frames[props.pendingFrom], frames[count - 1], d)}
                role="img"
                aria-label="Focos do INPE ainda não publicados para estes horários"
                title="Focos do INPE ainda não publicados para estes horários (chegam cerca de 20 min depois das imagens)."
              />
            )}
            {/* marcas de hora (meia-noite mais alta) */}
            {ticks.map((tk) => (
              <span
                key={tk.t}
                className={clsx(
                  'absolute w-px',
                  tk.major ? 'h-2 bg-ink-300' : 'h-1 bg-ink-500',
                  props.compact ? 'top-[18px]' : 'top-[15px]',
                )}
                style={{ left: pct(positionOf(tk.t, d)) }}
                aria-hidden
              />
            ))}
            {/* trechos sem imagem de cada camada (2+ camadas): dentro do trilho, sem criar outra barra */}
            <LayerGaps d={d} frames={frames} compact={props.compact} />
            {/* agora (só quando cai dentro do período dos quadros) */}
            {d.nowInside && (
              <span
                className={clsx('absolute w-px bg-ink-100', props.compact ? 'top-[4px] h-5' : 'top-[3px] h-4')}
                style={{ left: pct(positionOf(now, d)) }}
                role="img"
                aria-label={`Agora: ${formatTimeBrt(now)} (Brasília)`}
                title={`Agora: ${formatTimeBrt(now)} (Brasília).`}
              >
                <span className="absolute -top-1 -left-[3px] border-x-[3.5px] border-t-[4px] border-x-transparent border-t-ink-100" aria-hidden />
              </span>
            )}
            {/* cursor do quadro atual; vazado quando o cursor está na zona da previsão (aqui fica a imagem de origem) */}
            {currentTime !== null && (
              <span
                data-cursor-fora={props.cursorFora || undefined}
                className={clsx(
                  'absolute -translate-x-1/2 rounded-full',
                  props.cursorFora
                    ? 'size-3 border-2 border-mata-400 bg-ink-900'
                    : 'size-4 border-2 border-ink-900 bg-mata-400 shadow-[0_0_0_1px_var(--color-mata-400)]',
                  props.cursorFora ? (props.compact ? 'top-[8px]' : 'top-[5px]') : props.compact ? 'top-[6px]' : 'top-[3px]',
                )}
                style={{ left: pct(positionOf(currentTime, d)) }}
                aria-hidden
              />
            )}
            {/* balão ao passar o mouse */}
            {hover !== null && !dragging.current && (
              <span
                className="pointer-events-none absolute -top-6 -translate-x-1/2 rounded-md border border-ink-600 bg-ink-900 px-1.5 py-0.5 font-mono text-[10px] text-ink-100 tabular-nums"
                style={{ left: pct(positionOf(hover, d)) }}
                aria-hidden
              >
                {formatDayTimeBrt(hover)}
              </span>
            )}
          </>
        )}
        {props.footer && <div className="pointer-events-none absolute inset-x-0 bottom-0">{props.footer}</div>}
      </div>
    </div>
  );
}
